/**
 * Thai Menu Template
 * Colorful Asian-style menu design
 */
import React from "react";
import { FTU_LOGO_URL } from "../constants";

export function ThaiTemplate({ 
  menuRef,
  generatedMenu, 
  menuLanguage, 
  showPrix,
  customLogoUrl,
  pageOrientation = "portrait"
}) {
  if (!generatedMenu) return null;

  return (
    <div
      ref={menuRef}
      className="relative p-4"
      style={{
        width: pageOrientation === "landscape" ? "297mm" : "210mm",
        minHeight: pageOrientation === "landscape" ? "210mm" : "297mm",
        background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #F97316 100%)",
      }}
    >
      {/* Thai decorative border */}
      <div className="absolute inset-3 border-4 border-yellow-400/50 rounded-xl" />
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-6">
        {/* Header with Logo */}
        <div className="text-center mb-4">
          {customLogoUrl ? (
            <img 
              src={customLogoUrl} 
              alt="Logo personnalisé"
              className="h-20 mx-auto mb-2 drop-shadow-lg"
            />
          ) : (
            <div className="text-5xl mb-2">🍜</div>
          )}
          <h1 className="text-2xl font-bold text-white drop-shadow-lg">
            {generatedMenu.titre || "Notre Carte"}
          </h1>
          {generatedMenu.sous_titre && (
            <p className="text-yellow-200 text-sm italic">{generatedMenu.sous_titre}</p>
          )}
        </div>
        
        {/* Menu Grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-min">
          {/* Sections */}
          {generatedMenu.sections?.map((section, sIdx) => (
            <React.Fragment key={sIdx}>
              {/* Section Header */}
              <div className="col-span-2 md:col-span-3 mt-2 first:mt-0">
                <h2 className="text-lg font-bold text-yellow-300 text-center py-1 bg-black/30 rounded-lg backdrop-blur-sm">
                  {section.type === "entree" ? "🥗 Entrées" :
                   section.type === "plat" ? "🍜 Plats" :
                   section.type === "dessert" ? "🍨 Desserts" : "🥤 Boissons"}
                </h2>
              </div>
              
              {/* Dishes */}
              {section.plats?.map((plat, pIdx) => (
                <div 
                  key={pIdx}
                  className="bg-white/95 rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform"
                >
                  {/* Dish Photo */}
                  <div className="aspect-square bg-gradient-to-br from-orange-100 to-pink-100 relative overflow-hidden">
                    {plat.image_url ? (
                      <img 
                        src={plat.image_url} 
                        alt={plat.nom}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {section.type === "entree" ? "🥗" :
                         section.type === "plat" ? "🍜" :
                         section.type === "dessert" ? "🍨" : "🥤"}
                      </div>
                    )}
                    
                    {/* Price Badge */}
                    {showPrix && (
                      <div className={`absolute top-2 right-2 ${plat.original_prix && plat.original_prix !== plat.prix ? 'bg-orange-500' : 'bg-yellow-400'} text-black font-bold px-2 py-1 rounded-full text-sm shadow-lg`}>
                        {plat.original_prix && plat.original_prix !== plat.prix && (
                          <span className="text-xs line-through opacity-60 mr-1">{plat.original_prix.toFixed(0)}€</span>
                        )}
                        {plat.prix.toFixed(0)} €
                      </div>
                    )}
                    
                    {/* Dietary badges */}
                    <div className="absolute bottom-2 left-2 flex gap-1">
                      {plat.vegetarien && (
                        <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">V</span>
                      )}
                      {plat.vegan && (
                        <span className="bg-green-700 text-white text-xs px-1.5 py-0.5 rounded">VG</span>
                      )}
                      {plat.sans_gluten && (
                        <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded">SG</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Dish Info */}
                  <div className="p-2 text-center">
                    <h3 className="font-bold text-gray-800 text-sm leading-tight">{plat.nom}</h3>
                    {plat.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{plat.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-4 pt-3 border-t border-yellow-400/30">
          {/* Logo Food Truck United */}
          <img 
            src={FTU_LOGO_URL} 
            alt="Food Truck United" 
            className="h-8 mx-auto mb-2 brightness-0 invert opacity-70"
          />
          <p className="text-white/80 text-xs">
            {generatedMenu.food_truck?.nom} • {menuLanguage === "en" ? "Please inform us of any allergies" : "Pour toutes allergies, merci de nous consulter"}
          </p>
          <div className="flex justify-center gap-2 mt-1">
            <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">V</span>
            <span className="text-white/60 text-xs">Végétarien</span>
            <span className="bg-green-700 text-white text-xs px-1.5 py-0.5 rounded">VG</span>
            <span className="text-white/60 text-xs">Vegan</span>
            <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded">SG</span>
            <span className="text-white/60 text-xs">Sans Gluten</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThaiTemplate;
